import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { rialToToman } from "utils/generalUtils"
import { UNINITIALIZED, PENDING, FULFILLED, IRT } from "constants/index"
import { PathInfo } from "types/general.types"
import { useAppSelector } from "appRedux/hooks"
import { useCreateOrderMutation } from "appRedux/slices/orderApiSlice"
import { updateSelectedIpgId } from "appRedux/slices/orderSlice"
import { useInitiatePaymentMutation } from "appRedux/slices/paymentApiSlice"
import CTA from "components/atoms/CTA"
import IpgSection from "components/organisms/IpgSection"
import CheckoutDetailSection from "components/organisms/CheckoutDetailSection"
import LoadingPlaceholder from "./LoadingPlaceholder"

export default function CheckoutTemplate() {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const orderDetail = useAppSelector((state) => state.order.orderDetail)
  const selectedIpgId = useAppSelector((state) => state.order.selectedIpgId)

  const [triggerCreateOrder, createOrderResult] = useCreateOrderMutation()
  const [triggerInitialPayment, initiatePaymentResult] = useInitiatePaymentMutation()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  useEffect(() => {
    handleCreateOrder(currentPageInfo)
  }, [currentPageInfo?.dynamicPathParts])

  useEffect(() => {
    setIsLoading(createOrderResult.status === PENDING || createOrderResult.status === UNINITIALIZED)
  }, [createOrderResult])

  function handleCreateOrder(currentPageInfo: PathInfo) {
    if (!currentPageInfo?.dynamicPathParts) return
    const { productId } = currentPageInfo.dynamicPathParts
    if (!productId) return
    triggerCreateOrder({ productId })
  }

  function handleOnSelectedIpgChange(id: number) {
    dispatch(updateSelectedIpgId(id))
  }

  function handlePayButtonClicked() {
    if (!orderDetail?.uuid || !selectedIpgId) return
    triggerInitialPayment({
      uuid: orderDetail.uuid,
      ipgId: selectedIpgId
    })
  }

  return (
    isLoading ? <LoadingPlaceholder/> : (
      (createOrderResult.status === FULFILLED && createOrderResult.data) ? <Stack gap="30px" flexGrow={1} height="100%">
        {createOrderResult.data.ipgList && createOrderResult.data.ipgList.length > 0 ? (
          <IpgSection
            ipgList={createOrderResult.data.ipgList}
            onChange={handleOnSelectedIpgChange}
          />
        ) : null}
        <CheckoutDetailSection
          totalAmount={createOrderResult.data.totalAmount}
          planInfo={createOrderResult.data.product}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" marginTop="auto" gap="16px">
          <Stack flexShrink={0}>
            <TotalAmountLabel>{t("checkout.totalPayableAmount")}</TotalAmountLabel>
            <Stack direction="row" alignItems="center" gap="4px">
              <Price className="farsi-digits">{rialToToman(createOrderResult.data.totalAmount)}</Price>
              <Currency>{t(`currency.${IRT}`)}</Currency>
            </Stack>
          </Stack>
          <CTA
            variant="contained"
            style={{ maxWidth: "190px" }}
            loading={initiatePaymentResult.isLoading}
            onClick={handlePayButtonClicked}
          >
            {t("premium.pay")}
          </CTA>
        </Stack>
      </Stack> : null
    )
  )
}

const TotalAmountLabel = styled(Typography)(({ theme }) => ({
  fontSize: "1.3rem",
  color: theme.palette.gray["13"]
}))

const Price = styled("span")(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 500,
  color: theme.palette.gray["15"]
}))

const Currency = styled("span")(({ theme }) => ({
  fontSize: "1.1rem",
  color: theme.palette.gray["13"]
}))
